import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  publicPaymentSettingsService,
  type PublicPaymentSettingsRecord,
} from "@project/services/donate/payment-settings.service";
import { Copy, Smartphone, Building2, ShieldCheck } from "lucide-react";

const valueOrNull = (value?: string | null) => {
  const next = String(value ?? "").trim();
  return next.length > 0 ? next : null;
};

export function PaymentInfoCard() {
  const [s, setS] = useState<PublicPaymentSettingsRecord | null>(null);

  useEffect(() => {
    let active = true;
    void publicPaymentSettingsService
      .get()
      .then(data => {
        if (active) {
          setS(data ?? null);
        }
      })
      .catch(() => {
        if (active) {
          setS(null);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  if (!s) {
    return null;
  }

  const upiId = valueOrNull(s.upi_id);
  const qrCodeUrl = valueOrNull(s.qr_code_url);
  const accountNumber = valueOrNull(s.account_number);
  const accountName = valueOrNull(s.account_name);
  const ifscCode = valueOrNull(s.ifsc_code);
  const bankName = valueOrNull(s.bank_name);
  const branch = valueOrNull(s.branch);
  const panNumber = valueOrNull(s.pan_number);
  const registration80g = valueOrNull(s.registration_80g);
  const trustRegNumber = valueOrNull(s.trust_registration_number);
  const contactPhone = valueOrNull(s.contact_phone);
  const contactEmail = valueOrNull(s.contact_email);

  return (
    <div className="space-y-4">
      {upiId && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-primary">
            <Smartphone className="h-5 w-5 text-gold" /> Pay via UPI
          </h3>
          <div className="bg-muted/40 mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 font-mono text-sm">
            <span>{upiId}</span>
            <CopyButton text={upiId} />
          </div>
          {qrCodeUrl && (
            <img
              src={qrCodeUrl}
              alt="UPI QR"
              className="mx-auto mt-3 h-40 w-40 rounded-lg border border-border object-contain"
            />
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Use any UPI app — GPay, PhonePe, Paytm, BHIM.
          </p>
        </div>
      )}

      {accountNumber && (
        <div className="rounded-2xl border border-border bg-card p-6">
          <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-primary">
            <Building2 className="h-5 w-5 text-gold" /> Bank Transfer
          </h3>
          <div className="mt-3 space-y-2">
            {accountName && <Row label="Account Name" value={accountName} />}
            <Row label="Account No" value={accountNumber} />
            {ifscCode && <Row label="IFSC" value={ifscCode} />}
            {bankName && (
              <Row
                label="Bank"
                value={`${bankName}${branch ? `, ${branch}` : ""}`}
              />
            )}
            {panNumber && <Row label="PAN" value={panNumber} />}
            {registration80g && <Row label="80G Reg" value={registration80g} />}
            {trustRegNumber && (
              <Row label="Trust Registration" value={trustRegNumber} />
            )}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-gold bg-card p-6">
        <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-primary">
          <ShieldCheck className="h-5 w-5 text-gold" /> Tax Benefits
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Donations are eligible for{" "}
          <span className="font-semibold">80G income tax exemption</span>.
          Receipt is issued after payment confirmation.
        </p>
        {(Boolean(contactPhone) || Boolean(contactEmail)) && (
          <p className="mt-3 text-xs text-muted-foreground">
            Help: {contactPhone}
            {contactPhone && contactEmail ? " • " : ""}
            {contactEmail}
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/30 flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate font-mono text-sm font-semibold">{value}</div>
      </div>
      <CopyButton text={value} />
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text);
        toast.success("Copied");
      }}
      className="hover:bg-primary/10 grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:text-primary"
    >
      <Copy className="h-4 w-4" />
    </button>
  );
}
